import React, { useState, useRef, useCallback } from 'react';
import { Modal } from 'antd';
import { LibraryFile } from 'mock/folder.data';
import { uploadFile } from '../function/api';
import { mapConfirmedUploadToLibraryFile } from '../function/converter';

type UploadStatus = 'pending' | 'uploading' | 'done' | 'error';

interface UploadedFile {
  id: string;
  file: File;
  status: UploadStatus;
  errorMessage?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  folderId: number | null;
  // `rawFile` is the original File the user picked — handy for local preview
  // (e.g. the journal scenario thumbnail) without needing a back-end URL.
  onUploadSuccess: (file: LibraryFile, rawFile: File) => void;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

const UploadModal: React.FC<Props> = ({ open, onClose, folderId, onUploadSuccess }) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    const items: UploadedFile[] = Array.from(fileList).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      file,
      status: 'pending',
    }));
    setFiles((prev) => [...prev, ...items]);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const removeFile = (id: string) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const handleClose = () => {
    setFiles([]);
    setIsDragging(false);
    setIsUploading(false);
    onClose();
  };

  const handleUpload = async () => {
    const pending = files.filter((f) => f.status === 'pending');
    if (pending.length === 0) return;

    setIsUploading(true);

    const setStatus = (id: string, status: UploadStatus, errorMessage?: string) => {
      setFiles((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status, errorMessage } : f))
      );
    };

    const folderIdStr = folderId !== null ? String(folderId) : null;

    for (const item of pending) {
      setStatus(item.id, 'uploading');
      try {
        const uploadRes = await uploadFile(item.file, folderId);
        if (!uploadRes.status) {
          throw new Error(uploadRes.message || 'Tải lên thất bại.');
        }

        const libraryFile = mapConfirmedUploadToLibraryFile(uploadRes.data, item.file, folderIdStr);
        onUploadSuccess(libraryFile, item.file);
        setStatus(item.id, 'done');
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Tải lên thất bại.';
        setStatus(item.id, 'error', msg);
      }
    }

    setIsUploading(false);

    setFiles((prev) => {
      const hasErrors = prev.some((f) => f.status === 'error');
      if (!hasErrors) {
        handleClose();
      }
      return prev;
    });
  };

  const hasPending = files.some((f) => f.status === 'pending');
  const allDoneOrError = files.length > 0 && files.every((f) => f.status === 'done' || f.status === 'error');
  return (
    <Modal
      title="Tải lên"
      open={open}
      onCancel={handleClose}
      onOk={handleUpload}
      okText={isUploading ? 'Đang tải...' : 'Tải lên'}
      cancelText={allDoneOrError ? 'Đóng' : 'Hủy'}
      okButtonProps={{ disabled: !hasPending || isUploading, loading: isUploading }}
      cancelButtonProps={{ disabled: isUploading }}
      width={520}
    >
      <div className="upload-modal">
        <div
          className={`upload-modal__zone${isDragging ? ' upload-modal__zone--over' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => !isUploading && inputRef.current?.click()}
        >
          <svg className="upload-modal__zone-icon" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 16 12 12 8 16" />
            <line x1="12" y1="12" x2="12" y2="21" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
          </svg>
          <p className="upload-modal__zone-text">Kéo thả file vào đây hoặc</p>
          <button
            className="upload-modal__browse-btn"
            disabled={isUploading}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
          >
            Chọn file
          </button>
          <p className="upload-modal__zone-hint">Cho phép PDF, Word, Excel, Video, Ảnh</p>
          <input
            ref={inputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => addFiles(e.target.files)}
          />
        </div>

        {files.length > 0 && (
          <div className="upload-modal__file-list">
            {files.map(({ id, file, status, errorMessage }) => (
              <div key={id} className={`upload-modal__file-item upload-modal__file-item--${status}`}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span className="upload-modal__file-name" title={errorMessage}>{file.name}</span>
                <span className="upload-modal__file-size">{formatSize(file.size)}</span>
                {status === 'uploading' && (
                  <span className="upload-modal__file-status upload-modal__file-status--uploading">Đang tải...</span>
                )}
                {status === 'done' && (
                  <span className="upload-modal__file-status upload-modal__file-status--done">✓</span>
                )}
                {status === 'error' && (
                  <span className="upload-modal__file-status upload-modal__file-status--error" title={errorMessage}>✗</span>
                )}
                {status === 'pending' && !isUploading && (
                  <button className="upload-modal__file-remove" onClick={() => removeFile(id)}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UploadModal;
